/**
 * The buyer or supplier record behind a company's data buyer or data seller
 * role: one editor for both, used from the company page and the filtered
 * company lists.
 *
 * Plan D7 says the near-identical CRUD modals of the Alpine app should be one
 * config-driven editor; this is it. Buyers and suppliers no longer have list
 * pages of their own (they are companies with a role), but the record keeps
 * every field it had: routing, prices, returns and statements read it by its
 * own id. Adding a field is one entry in FIELDS.
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Group, JsonInput, Modal, Stack, Switch, TextInput } from '@mantine/core';
import { useSaveEntity, type EntityInput, type EntityKind } from '../api/queries';
import type { components } from '../api/schema';
import { useVocab, type Noun } from '../vocab';
import { SupplierCostFields, readSupplierCost, writeSupplierCost } from './SupplierCost';
import { BuyerReturnsFields, SupplierReturnsFields } from './EntityBilling';

export type Entity = components['schemas']['Entity'];

interface FieldSpec {
  key: 'name' | 'email' | 'phone';
  label: string;
  required?: boolean;
  placeholder?: string;
}

const FIELDS: FieldSpec[] = [
  { key: 'name', label: 'Name', required: true },
  { key: 'email', label: 'Email', placeholder: 'optional' },
  { key: 'phone', label: 'Phone', placeholder: 'optional' },
];

export const ENTITY_NOUN: Record<EntityKind, Noun> = { buyers: 'buyer', suppliers: 'supplier' };

type Draft = EntityInput & { settingsText: string };

const EMPTY: Draft = { name: '', email: '', phone: '', active: true, settingsText: '{}' };

function draftOf(e: Entity | null, name?: string): Draft {
  if (!e) return { ...EMPTY, name: name ?? '' };
  return {
    name: e.name,
    email: e.email,
    phone: e.phone,
    active: e.active,
    settingsText: JSON.stringify(e.settings ?? {}, null, 2),
  };
}

/**
 * Create (`entity` null) or edit a buyer or supplier record. A new record
 * gets its company in the same write, on the server: one of the same name in
 * this organisation that does not hold the role yet, or a new one.
 */
export function EntityRecordModal({
  kind, entity, opened, onClose, onSaved, initialName,
}: {
  kind: EntityKind;
  entity: Entity | null;
  opened: boolean;
  onClose: () => void;
  onSaved?: (saved: Entity) => void;
  /** For a new record: the name to start from. */
  initialName?: string;
}) {
  const v = useVocab();
  const noun = ENTITY_NOUN[kind];
  const isSupplier = kind === 'suppliers';
  const save = useSaveEntity(kind);
  const [draft, setDraft] = useState<Draft>(() => draftOf(entity, initialName));
  const [formError, setFormError] = useState('');
  useEffect(() => {
    if (opened) {
      setDraft(draftOf(entity, initialName));
      setFormError('');
    }
  }, [opened, entity, initialName]);

  // The cost and returns fields edit the same settings JSON the box below shows.
  let parsedSettings: unknown = undefined;
  try {
    parsedSettings = draft.settingsText.trim() ? JSON.parse(draft.settingsText) : {};
  } catch {
    parsedSettings = undefined;
  }

  function submit() {
    if (!draft.name.trim()) {
      setFormError('Name is required');
      return;
    }
    let settings: unknown = {};
    try {
      settings = draft.settingsText.trim() ? JSON.parse(draft.settingsText) : {};
    } catch {
      setFormError('Settings must be valid JSON');
      return;
    }
    setFormError('');
    save.mutate(
      {
        ...(entity ? { id: entity.id } : {}),
        body: {
          name: draft.name.trim(),
          email: draft.email ?? '',
          phone: draft.phone ?? '',
          active: draft.active ?? true,
          settings,
        },
      },
      {
        onSuccess: (saved) => {
          onSaved?.(saved as Entity);
          onClose();
        },
        // The server is the authority on whether this saved. Showing the
        // thrown message beats a blanket "failed".
        onError: (e) => setFormError(e instanceof Error ? e.message : 'Save failed'),
      },
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={entity ? `Edit ${v.one(noun)} record` : `New ${isSupplier ? v.p.dataSellerLower : v.p.dataBuyerLower}`}
    >
      <Stack>
        {formError && <Alert color="red" variant="light">{formError}</Alert>}
        {FIELDS.map((f) => (
          <TextInput
            key={f.key}
            label={f.label}
            required={f.required}
            placeholder={f.placeholder}
            value={draft[f.key] ?? ''}
            onChange={(ev) => setDraft({ ...draft, [f.key]: ev.currentTarget.value })}
          />
        ))}
        <Switch
          label="Active"
          description={`Off: routing stops using this ${v.one(noun)}; its history stays.`}
          checked={draft.active ?? true}
          onChange={(ev) => setDraft({ ...draft, active: ev.currentTarget.checked })}
        />
        {isSupplier && (
          <SupplierCostFields
            value={readSupplierCost(parsedSettings)}
            disabled={parsedSettings === undefined}
            onChange={(cost) =>
              setDraft({
                ...draft,
                settingsText: JSON.stringify(writeSupplierCost(parsedSettings, cost), null, 2),
              })
            }
          />
        )}
        {isSupplier ? (
          <SupplierReturnsFields
            settings={parsedSettings}
            disabled={parsedSettings === undefined}
            onChange={(next) => setDraft({ ...draft, settingsText: JSON.stringify(next, null, 2) })}
          />
        ) : (
          <BuyerReturnsFields
            settings={parsedSettings}
            disabled={parsedSettings === undefined}
            onChange={(next) => setDraft({ ...draft, settingsText: JSON.stringify(next, null, 2) })}
          />
        )}
        <JsonInput
          label="Settings"
          description={`Free-form JSON. On ${v.many('buyer')} this holds price, daily_cap, delivery_method and endpoint.`}
          autosize
          minRows={4}
          formatOnBlur
          value={draft.settingsText}
          onChange={(text) => setDraft({ ...draft, settingsText: text })}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={save.isPending}>
            {entity ? 'Save' : 'Create'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
